import { createSignal, For, Show } from "solid-js";
import { ButtonMonochrome } from "@ui/Button";
import { InputContainer } from "@ui/InputContainer";
import { BrandIcon } from "@ui/BrandIcon";
import { HStack, VStack } from "@ui/Stack";
import { Heading, HeavyHeading } from "@ui/typography/Heading";
import { Text, Link } from "@ui/typography/Text";
import { TextMono } from "@ui/typography/TextMono";
import { Video } from "@ui/Video";
import askGVTLogo from "@ui/icons/AskGVTLogo.svg?raw";
import studioLogo from "@ui/icons/GVTStudioLogo.svg?raw";
import rosterLogo from "@ui/icons/GVTRosterLogo.svg?raw";
import gvtLabsLogo from "@ui/icons/GVTLabsLogo.svg?raw";
import { getCookie } from "~/modules/shared/utils/auth";
import { InviteCodeCard } from "./InviteCodeCard";

type AuthService = {
	id: string;
	label: string;
	icon?: string;
};

type Props = {
	serviceName: string;
	authServices: AuthService[];
	emailSignInEnabled: boolean;
	stateID?: string;
	signupCode: string | null;
	presentation: "full" | "app-embedded";
};

const domainRejectedMessage =
	"We are not currently accepting sign-ups from your email domain.";

const signupCodeExpiredMessage =
	"Your sign-up code has expired. Please request a new one.";

const authFailureMessage =
	"We couldn't complete sign-in. Please try again.";

const noAccessMessage =
	"This account doesn't have access to this site.";

const linkUsedMessage =
	"This sign-in link has already been used. Enter your email to get a new one.";

const linkExpiredMessage =
	"This sign-in link has expired or been replaced by a newer one. Enter your email to get a new one.";

type SignInArea = "askgvt" | "studio" | "roster";

type SignInBranding = {
	logo: string;
	title: string;
	subtitle: string;
};

const signInAreaFromSearch = (params: URLSearchParams): SignInArea => {
	const area = params.get("area");
	if (area === "studio" || area === "roster") {
		return area;
	}
	return "askgvt";
};

const ASKGVT_SIGN_IN_BRANDING: Record<SignInArea, SignInBranding> = {
	askgvt: {
		logo: askGVTLogo,
		title: "Ask video anything.",
		subtitle: "Watch the moment, not a summary.",
	},
	studio: {
		logo: studioLogo,
		title: "Ask your videos anything.",
		subtitle: "Every moment, across every video you've made.",
	},
	roster: {
		logo: rosterLogo,
		title: "Ask your roster anything.",
		subtitle: "Across every video your creators have ever made.",
	},
};

const GVTLABS_SIGN_IN_BRANDING: SignInBranding = {
	logo: gvtLabsLogo,
	title: "Ask video anything.",
	subtitle: "Watch the moment, not a summary.",
};

export const SignInApp = (props: Props) => {
	const parameters = new URLSearchParams(window.location.search);
	if (parameters.has("action")) {
		parameters.delete("action");
		window.history.replaceState(null, "", `/sign-in?${parameters}`);
	}

	const [email, setEmail] = createSignal("");
	const [website, setWebsite] = createSignal("");
	const [submitting, setSubmitting] = createSignal(false);
	const [submitted, setSubmitted] = createSignal(false);
	const [passwordRequired, setPasswordRequired] = createSignal(false);
	const [password, setPassword] = createSignal("");
	const [errorMessage, setErrorMessage] = createSignal("");
	const [urlReason, setUrlReason] = createSignal(parameters.get("reason"));
	const [signupCode, setSignupCode] = createSignal(props.signupCode);
	const [inviteCodeCardOpen, setInviteCodeCardOpen] = createSignal(false);
	let inviteCodeTrigger: HTMLButtonElement | undefined;
	const pageLoadedAt = Math.floor(Date.now() / 1000);

	const isValidEmail = () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email());
	const hasError = () =>
		urlReason() === "error" ||
		urlReason() === "domain_not_allowed" ||
		urlReason() === "signup_code_expired" ||
		urlReason() === "no_access" ||
		urlReason() === "link_used" ||
		urlReason() === "link_expired";
	const urlReasonMessage = () => {
		switch (urlReason()) {
			case "domain_not_allowed":
				return domainRejectedMessage;
			case "signup_code_expired":
				return signupCodeExpiredMessage;
			case "no_access":
				return noAccessMessage;
			case "link_used":
				return linkUsedMessage;
			case "link_expired":
				return linkExpiredMessage;
			default:
				return authFailureMessage;
		}
	};
	const client = parameters.get("client");

	// iOS zooms the web view when focusing an input whose computed font size is
	// under 16px. Keep this in px: rem/em values follow the root or parent font
	// size and can silently drop below the threshold. Inline so that shared
	// input styles can't out-specify it.
	const embeddedInputStyle = () =>
		props.presentation === "app-embedded" ? { "font-size": "16px" } : undefined;

	const branding = () =>
		props.serviceName === "AskGVT"
			? ASKGVT_SIGN_IN_BRANDING[signInAreaFromSearch(parameters)]
			: GVTLABS_SIGN_IN_BRANDING;

	const clearErrors = () => {
		setErrorMessage("");
		parameters.delete("reason");
		setUrlReason(null);
	};

	const handleAuthClick = (service: AuthService) => {
		const authParams = new URLSearchParams();

		if (client) {
			authParams.set("client", client);
		}

		const codeChallenge = parameters.get("code_challenge");
		if (codeChallenge) {
			authParams.set("code_challenge", codeChallenge);
		}

		const query = authParams.toString();
		window.location.href = query
			? `/auth/${service.id}?${query}`
			: `/auth/${service.id}`;
	};

	const cancelPasswordEntry = () => {
		setPasswordRequired(false);
		setPassword("");
		clearErrors();
	};

	const handleEmailAuth = async (withPassword = false) => {
		if (submitting()) return;
		setSubmitting(true);
		clearErrors();
		window.history.replaceState(null, "", `/sign-in?${parameters}`);

		try {
			const body: Record<string, unknown> = {
				email: email(),
				website: website(),
				state: props.stateID,
				_loaded_at: pageLoadedAt,
				...(client && { client }),
			};
			if (withPassword) {
				body.password = password();
			}

			const response = await fetch("/auth/email", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"Accept": "application/json",
					"X-XSRF-TOKEN": getCookie("XSRF-TOKEN") ?? "",
				},
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (response.status === 429) {
				setErrorMessage("Too many attempts. Please wait a moment and try again.");
			} else if (data.reason === "domain_not_allowed") {
				setErrorMessage(domainRejectedMessage);
			} else if (data.reason === "signup_code_expired") {
				setErrorMessage(signupCodeExpiredMessage);
			} else if (data.requires === "password") {
				setPasswordRequired(true);
			} else if (data.redirect) {
				window.location.href = data.redirect;
			} else if (!response.ok) {
				setErrorMessage(data.message || "Something went wrong. Please try again.");
			} else {
				setSubmitted(true);
			}
		} catch {
			setErrorMessage("Unable to connect. Please check your connection and try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<VStack class="wrapper" align="center" spacing="none" fullWidth fullHeight>
				<HStack class="sign-in-panel" spacing="none" fullHeight>
					<div class="column details">
						<VStack class="" spacing="xlarge" align="center" fullWidth fullHeight>
							<span class="logo" innerHTML={branding().logo} />

							<Show
								when={!submitted() && !passwordRequired()}
								fallback={
									<Show
										when={passwordRequired()}
										fallback={
											<VStack class="check-email" spacing="large" align="center">
												<Heading as="h2" center>Check Your Email</Heading>
												<VStack spacing="xlarge" align="center">
													<Text color="fg2" smarten center>
														You'll receive a sign-in link shortly. If you don't receive an email, your address might not be associated with an account or we couldn't create one for you.</Text>
													<Text color="fg2" smarten center>If you think you've mistyped your address, please <Link href={`/sign-in?${parameters}`} color="ink" hoverColor="ink" underlined>try&nbsp;again</Link>.</Text>
												</VStack>
											</VStack>
										}
									>
										<VStack class="email-sign-in" spacing="large">
											<Heading as="h2" center>Enter password</Heading>
											<Text color="fg2" center>Please enter the password for <span class="email-address">{email()}</span></Text>

											<VStack spacing="medium">
												<InputContainer>
													<input
														type="password"
														placeholder="Password"
														style={embeddedInputStyle()}
														value={password()}
														onInput={(e) => setPassword(e.currentTarget.value)}
														onKeyDown={(e) => {
															if (e.key === "Enter" && password()) handleEmailAuth(true);
														}}
														autocomplete="current-password"
													/>
												</InputContainer>

												<Show when={errorMessage()}>
													<Text color="error" size="small">{errorMessage()}</Text>
												</Show>
											</VStack>

											<ButtonMonochrome
												variant="secondary"
												size="large"
												fullWidth
												disabled={!password() || submitting()}
												onClick={() => handleEmailAuth(true)}
												class="main-button"
											>
												{submitting()
													? "Please wait\u2026"
													: <span>Log in</span>}
											</ButtonMonochrome>

											<ButtonMonochrome
												variant="secondary"
												size="large"
												fullWidth
												disabled={submitting()}
												onClick={cancelPasswordEntry}
												class="main-button"
											>
												<span>Cancel</span>
											</ButtonMonochrome>
										</VStack>
									</Show>
								}
							>

								<VStack spacing="medium" align="center">
									<HeavyHeading smarten class="title">{branding().title}</HeavyHeading>

									<Show when={props.presentation === "full"}>
										<Heading as="h3" center smarten>{branding().subtitle}</Heading>
									</Show>
								</VStack>

								<VStack spacing="large" align="center" fullWidth>
									<Show
										when={hasError()}
										fallback={<Text center>Log in or sign up</Text>}
									>
										<Text color="error" center>
											{urlReasonMessage()}
										</Text>
									</Show>

									<VStack spacing="medium" fullWidth>
										<For each={props.authServices}>
											{(service) => (
												<ButtonMonochrome
													class="auth-service"
													fullWidth
													onClick={() =>
														handleAuthClick(service)
													}
												>
													<BrandIcon
														platform={
															service.icon ?? service.id
														}
														fillWithCurrentColor={false}
													/>
													<span class="label">
														{`Continue with ${service.label}`}
													</span>
												</ButtonMonochrome>
											)}
										</For>
									</VStack>
								</VStack>

								<Show when={props.emailSignInEnabled}>
									<VStack class="email-sign-in" spacing="large" fullWidth>
										<HStack spacing="large" align="center" justify="center">
											<hr class="separator" />
											<TextMono transform="uppercase" color="fg2" class="or">or</TextMono>
											<hr class="separator" />
										</HStack>

										<VStack spacing="medium" fullWidth>
											<InputContainer>
												<input
													type="hidden"
													name="website"
													value={website()}
													onInput={(e) => setWebsite(e.currentTarget.value)}
													tabindex={-1}
													autocomplete="off"
													aria-hidden="true"
												/>

												<input
													type="email"
													placeholder="Email address"
													style={embeddedInputStyle()}
													value={email()}
													onInput={(e) => setEmail(e.currentTarget.value)}
													onKeyDown={(e) => {
														if (e.key === "Enter" && isValidEmail()) handleEmailAuth();
													}}
													autocomplete="email"
												/>
											</InputContainer>

											<Show when={errorMessage()}>
												<Text color="error" size="small">{errorMessage()}</Text>
											</Show>
										</VStack>

										<ButtonMonochrome
											fullWidth
											class="auth-service"
											size="large"
											disabled={!isValidEmail() || submitting()}
											onClick={() => handleEmailAuth()}
										>
											{submitting()
												? "Please wait\u2026"
												: <span>Continue with email</span>
											}
										</ButtonMonochrome>
									</VStack>
								</Show>
							</Show>


							<VStack class="footer" align="center" spacing="large" fullWidth>
								<Show
									when={signupCode()}
									fallback={
										<Show when={props.serviceName === "AskGVT"}>
											<div class="invite-code-trigger">
												<button
													ref={inviteCodeTrigger}
													type="button"
													class="invite-code-trigger__button"
													aria-haspopup="dialog"
													aria-expanded={inviteCodeCardOpen()}
													onClick={() => setInviteCodeCardOpen(true)}
												>
													<Text as="span" weight="bold" classList={{ "gvtui-text--underlined": true }}>
														I have an invite code
													</Text>
												</button>
												<Show when={inviteCodeCardOpen()}>
													<InviteCodeCard
														inputStyle={embeddedInputStyle()}
														onAccepted={(code) => {
															setSignupCode(code);
															setInviteCodeCardOpen(false);
														}}
														onCancel={() => {
															setInviteCodeCardOpen(false);
															inviteCodeTrigger?.focus();
														}}
													/>
												</Show>
											</div>
										</Show>
									}
								>
									<Text weight="bold" center>Using invite code: {signupCode()}</Text>
								</Show>
								<Text size="small" color="fg2" center>
									By continuing, you agree to our <Link href="https://askgvt.com/terms" color="ink" hoverColor="ink" underlined>terms</Link> and <Link href="https://askgvt.com/privacy" color="ink" hoverColor="ink" underlined>privacy&nbsp;policy</Link>.
								</Text>
							</VStack>

						</VStack>
					</div>

					<Show when={props.presentation === "full"}>
						<div class="column image sign-in">
							<HStack align="center" justify="center">
								<Video
									src="/sign-in-video.mp4"
									autoplay
									muted
									loop
									playsinline
									aria-hidden="true"
								/>
							</HStack>
						</div>
					</Show>
				</HStack>
			</VStack>

			<Show when={props.presentation === "full"}>
				<footer>
					<Text color="fg2" center>AskGVT &copy; 2026</Text>
				</footer>
			</Show>
		</>
	);
};
